import { useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, Tag } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { blogPosts, blogCategories } from "../data/blogData";
import { SEOHead } from "./SEOHead";

export const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");

  const filteredPosts = selectedCategory === "all" 
    ? blogPosts 
    : blogPosts.filter(post => post.category.toLowerCase().replace(" ", "-") === selectedCategory);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title="Car Recovery Blog Hackney | Transport Tips & Guides | Ovawell Autos"
        description="Expert advice on car recovery, vehicle transport, and auction car collection in Hackney & East London. Read guides, tips, and industry insights from local professionals."
        keywords="car recovery blog, vehicle transport tips, hackney car advice, east london auto blog, auction transport guide, breakdown recovery help"
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <Badge className="bg-slate-700 text-white mb-4">Blog & Resources</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Car Recovery & Transport Insights for Hackney & East London
            </h1>
            <p className="text-xl text-slate-300">
              Expert guides, tips, and advice from Hackney's trusted car recovery professionals. Learn everything about vehicle transport, breakdown recovery, and auction car collection in East London.
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-4">Categories</h3>
                <div className="space-y-2">
                  {blogCategories.map((category) => (
                    <button
                      key={category.slug}
                      onClick={() => setSelectedCategory(category.slug)}
                      className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                        selectedCategory === category.slug
                          ? "bg-slate-900 text-white"
                          : "hover:bg-slate-100 text-slate-700"
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span>{category.name}</span>
                        <span className="text-sm text-slate-500">{category.count}</span>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="mt-8 p-4 bg-slate-900 rounded-lg text-white">
                  <h4 className="font-bold mb-2">Need Help Now?</h4>
                  <p className="text-sm text-slate-300 mb-3">
                    24/7 car recovery across Hackney & East London
                  </p>
                  <a href="tel:07492639223">
                    <Button className="w-full bg-white text-slate-900 hover:bg-slate-100">
                      Call 07492 639223
                    </Button>
                  </a>
                </div>

                <div className="mt-6">
                  <h4 className="font-bold mb-3">Popular Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline">Hackney</Badge>
                    <Badge variant="outline">East London</Badge>
                    <Badge variant="outline">Car Recovery</Badge>
                    <Badge variant="outline">Breakdown</Badge>
                    <Badge variant="outline">Auction Transport</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </aside>

          {/* Blog Posts */}
          <div className="lg:col-span-3">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-slate-900">
                {selectedCategory === "all" ? "All Posts" : blogCategories.find(c => c.slug === selectedCategory)?.name}
              </h2>
              <p className="text-slate-600 mt-1">
                {filteredPosts.length} article{filteredPosts.length !== 1 ? "s" : ""} found
              </p>
            </div>

            <div className="space-y-8">
              {filteredPosts.map((post) => (
                <Card key={post.id} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="md:col-span-1">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-64 md:h-full object-cover"
                      />
                    </div>
                    <CardContent className="md:col-span-2 p-6">
                      <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(post.date).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {post.readTime}
                        </div>
                        <Badge variant="outline">{post.category}</Badge>
                      </div>

                      <h3 className="text-2xl font-bold text-slate-900 mb-3 hover:text-slate-700 transition-colors">
                        <Link to={`/blog/${post.slug}`}>
                          {post.title}
                        </Link>
                      </h3>

                      <p className="text-slate-600 mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>

                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-2">
                          {post.tags.slice(0, 3).map((tag, index) => (
                            <div key={index} className="flex items-center gap-1 text-xs text-slate-500">
                              <Tag className="w-3 h-3" />
                              {tag}
                            </div>
                          ))}
                        </div>
                        <Link to={`/blog/${post.slug}`}>
                          <Button variant="outline" className="gap-2">
                            Read More
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              ))}
            </div>

            {filteredPosts.length === 0 && (
              <Card className="p-12 text-center">
                <p className="text-slate-600">No articles found in this category.</p>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <section className="bg-slate-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Car Recovery in Hackney?</h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            24/7 emergency breakdown recovery, auction transport, and vehicle transportation across East London
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:07492639223">
              <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
                Call 07492 639223
              </Button>
            </a>
            <Link to="/#quote">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900">
                Get a Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
